/**
 * Character Counter Component
 * Displays character count for text inputs with visual feedback
 * Adapted from prototype for Shopify theme integration
 */

class BundleCharacterCounter {
  constructor(currentLength = 0, maxLength = 7, warningThreshold = null) {
    this.currentLength = currentLength;
    this.maxLength = maxLength;
    // Default warning threshold is when at maxLength - 1
    this.warningThreshold = warningThreshold !== null ? warningThreshold : maxLength - 1;
  }

  /**
   * Render the character counter
   * @returns {string} HTML string
   */
  render() {
    const stateClass = this.getStateClass();
    const ariaLabel = `${this.currentLength} of ${this.maxLength} characters used`;

    return `
      <span
        class="bundle-character-counter ${stateClass}"
        data-current="${this.currentLength}"
        data-max="${this.maxLength}"
        aria-label="${ariaLabel}"
        role="status"
        aria-live="polite"
      >
        ${this.currentLength}/${this.maxLength}
      </span>
    `;
  }

  /**
   * Determine the state class based on current length
   * @returns {string} State modifier class
   */
  getStateClass() {
    if (this.currentLength >= this.maxLength) {
      return 'bundle-character-counter--error';
    }

    if (this.currentLength >= this.warningThreshold) {
      return 'bundle-character-counter--warning';
    }

    return '';
  }

  /**
   * Update the character count
   * @param {number} newLength - New character count
   */
  updateCount(newLength) {
    if (newLength < 0) return;
    this.currentLength = newLength;
  }

  /**
   * Get the current character count
   * @returns {number} Current character count
   */
  getCurrentLength() {
    return this.currentLength;
  }

  /**
   * Check if the character count is at or over the limit
   * @returns {boolean} True if at or over limit
   */
  isAtLimit() {
    return this.currentLength >= this.maxLength;
  }

  /**
   * Get the number of remaining characters
   * @returns {number} Remaining characters
   */
  getRemainingCharacters() {
    return Math.max(0, this.maxLength - this.currentLength);
  }

  /**
   * Static helper method to attach counter to an input element
   * @param {HTMLInputElement} inputElement - The input element to attach to
   * @param {HTMLElement} counterElement - The counter display element
   * @param {number} maxLength - Maximum allowed characters
   */
  static attachToInput(inputElement, counterElement, maxLength) {
    if (!inputElement || !counterElement) {
      console.error('[CharacterCounter] Invalid input or counter element provided');
      return null;
    }

    const counter = new BundleCharacterCounter(inputElement.value.length, maxLength);
    counterElement.innerHTML = counter.render();

    // Update counter on input
    inputElement.addEventListener('input', (e) => {
      const newLength = e.target.value.length;
      counter.updateCount(newLength);
      counterElement.innerHTML = counter.render();
    });

    return counter;
  }
}

// Export to global scope
window.BundleCharacterCounter = BundleCharacterCounter;
